import React, { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { MapContainer, TileLayer, Polygon, Polyline, Pane, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { Maximize2, Minimize2 } from 'lucide-react';
import { normalizePolygonFromApi, toLeafletFormat } from '../../utils/polygonUtils';

// Fix pour les icônes Leaflet par défaut
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

/**
 * Composant pour dessiner/éditer un polygone sur une carte Leaflet
 */
const ZonePolygonEditor = ({ 
  initialPolygon = null, 
  existingZones = [], // Zones existantes à afficher en référence (grisées)
  mapInstanceKey = 'default', // Key stable: change UNIQUEMENT à l'ouverture de la modale
  onPolygonChange, 
  center = [48.8566, 2.3522], // Paris par défaut
  zoom = 13,
  height = '400px'
}) => {
  const [polygon, setPolygon] = useState([]);
  const [isDrawing, setIsDrawing] = useState(false);
  const [isPanning, setIsPanning] = useState(false); // Pan “pause” pendant le dessin
  const [isFullscreen, setIsFullscreen] = useState(false);
  const mapRef = useRef(null); // Référence à l'instance Leaflet Map
  const mapInstanceRef = useRef(null); // Référence à l'instance MapContainer de React-Leaflet
  const markersRef = useRef([]);

  // Référence pour suivre le dernier initialPolygon traité
  const lastInitialPolygonRef = useRef(null);
  const isMountedRef = useRef(false);
  const isDrawingRef = useRef(false);
  
  // Synchroniser isDrawingRef avec isDrawing
  useEffect(() => {
    isDrawingRef.current = isDrawing;
  }, [isDrawing]);
  
  // Initialiser le polygone depuis les props UNIQUEMENT au montage initial
  // Ne PAS reset isDrawing si on est en train de dessiner (pour éviter de casser le dessin)
  useEffect(() => {
    // PROTECTION: Ne JAMAIS reset isDrawing si on est en train de dessiner
    if (isDrawingRef.current) {
      return; // Ignorer complètement les changements d'initialPolygon pendant le dessin
    }
    
    // Au premier montage uniquement
    if (!isMountedRef.current) {
      if (initialPolygon) {
        const normalized = normalizePolygonFromApi(initialPolygon);
        setPolygon(normalized);
      } else {
        setPolygon([]);
      }
      setIsDrawing(false);
      lastInitialPolygonRef.current = initialPolygon;
      isMountedRef.current = true;
      return;
    }
    
    // Après le montage, seulement si initialPolygon change vraiment (nouvelle zone chargée)
    // Comparer avec la référence pour éviter les resets inutiles
    const currentPolygonStr = JSON.stringify(initialPolygon);
    const lastPolygonStr = JSON.stringify(lastInitialPolygonRef.current);
    
    // Seulement si initialPolygon a vraiment changé
    if (currentPolygonStr !== lastPolygonStr) {
      if (initialPolygon) {
        const normalized = normalizePolygonFromApi(initialPolygon);
        setPolygon(normalized);
      } else {
        setPolygon([]);
      }
      setIsDrawing(false);
      lastInitialPolygonRef.current = initialPolygon;
    }
  }, [initialPolygon]);

  // Flag pour éviter les appels multiples à invalidateSize
  const hasInvalidatedRef = useRef(false);

  // Recalculer la taille de la carte UNE SEULE FOIS après le montage complet
  useEffect(() => {
    if (mapRef.current && !hasInvalidatedRef.current) {
      // Utiliser requestAnimationFrame pour s'assurer que le DOM est prêt
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (mapRef.current && !mapRef.current._destroyed) {
            mapRef.current.invalidateSize();
            hasInvalidatedRef.current = true;
          }
        }, 100);
      });
    }
  }, [mapRef.current]); // Se déclenche quand la carte est créée

  // Recalculer la taille quand on change de mode plein écran
  useEffect(() => {
    if (mapRef.current && !isFullscreen) {
      // Reset le flag pour permettre un nouveau calcul
      hasInvalidatedRef.current = false;
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (mapRef.current && !mapRef.current._destroyed) {
            mapRef.current.invalidateSize();
            hasInvalidatedRef.current = true;
          }
        }, 150);
      });
    }
  }, [isFullscreen]);

  // Notifier le parent des changements (avec debounce pour éviter les re-renders excessifs)
  useEffect(() => {
    if (onPolygonChange) {
      const timeoutId = setTimeout(() => {
        onPolygonChange(polygon);
      }, 100);
      return () => clearTimeout(timeoutId);
    }
  }, [polygon, onPolygonChange]);

  // Ajuster les interactions carte selon les modes (dessin / pan)
  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;

    if (isDrawing && !isPanning) {
      // Mode dessin : on évite les “sauts” => pas de drag
      map.dragging.disable();
      map.doubleClickZoom.disable();
      // On garde la molette + boutons +/- (utile pour dessiner précisément)
      map.scrollWheelZoom.enable();
    } else {
      // Mode normal OU mode pan
      map.dragging.enable();
      map.doubleClickZoom.enable();
      map.scrollWheelZoom.enable();
    }

    return () => {
      // Toujours remettre dans un état normal au démontage
      try {
        map.dragging.enable();
        map.doubleClickZoom.enable();
        map.scrollWheelZoom.enable();
      } catch (e) {
        // ignore
      }
    };
  }, [isDrawing, isPanning]);

  // FitBounds / setView : 1 SEULE FOIS à l'ouverture (via mapInstanceKey)
  useEffect(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const polygons = (existingZones || [])
      .filter(z => z?.polygon)
      .map(z => normalizePolygonFromApi(z.polygon))
      .filter(arr => Array.isArray(arr) && arr.length > 0);

    if (polygons.length > 0) {
      const all = polygons.flat();
      const bounds = L.latLngBounds(all.map(p => [p.lat, p.lng]));
      if (bounds.isValid()) {
        // padding pour laisser respirer la zone
        map.fitBounds(bounds, { padding: [24, 24] });
        return;
      }
    }

    // fallback: centre fourni (commercant / par défaut)
    map.setView(center, zoom);
  }, [mapInstanceKey]); // IMPORTANT: PAS de dépendance à polygon

  // Composant pour écouter les clics sur la carte (ajout de points)
  const MapClickHandler = () => {
    useMap(); // garantit l'attachement au bon map

    useMapEvents({
      click: (e) => {
        // En mode dessin, si on n'est pas en “pan”, on ajoute un point
        if (isDrawing && !isPanning) {
          if (e.originalEvent) {
            // Ne pas laisser l'event remonter (évite comportements parasites)
            e.originalEvent.stopPropagation();
          }

          const newPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
          setPolygon((prev) => [...prev, newPoint]);
        }
      },
      // Empêcher le double-clic zoom pendant le dessin
      dblclick: (e) => {
        if (isDrawing && e.originalEvent) {
          e.originalEvent.preventDefault();
          e.originalEvent.stopPropagation();
        }
      }
    });
    return null;
  };

  // Effacer le polygone
  const handleClear = useCallback(() => {
    setPolygon([]);
    markersRef.current.forEach(marker => {
      if (marker && mapRef.current) {
        mapRef.current.removeLayer(marker);
      }
    });
    markersRef.current = [];
  }, []);

  // Supprimer le dernier point
  const handleRemoveLastPoint = useCallback(() => {
    if (polygon.length > 0) {
      const newPolygon = polygon.slice(0, -1);
      setPolygon(newPolygon);
      
      if (markersRef.current.length > 0) {
        const lastMarker = markersRef.current.pop();
        if (lastMarker && mapRef.current) {
          mapRef.current.removeLayer(lastMarker);
        }
      }
    }
  }, [polygon]);

  // Fermer le polygone
  const handleClosePolygon = useCallback(() => {
    if (polygon.length >= 3) {
      setIsDrawing(false);
    }
  }, [polygon.length]);

  // Désactiver le drag immédiatement quand on active le mode dessin
  useEffect(() => {
    if (mapRef.current) {
      if (isDrawing) {
        // Désactiver IMMÉDIATEMENT le drag
        mapRef.current.dragging.disable();
        mapRef.current.doubleClickZoom.disable();
      } else {
        // Réactiver quand on arrête de dessiner
        mapRef.current.dragging.enable();
        mapRef.current.doubleClickZoom.enable();
      }
    }
  }, [isDrawing]);

  // Basculer le mode plein écran
  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
    setTimeout(() => {
      if (mapRef.current) {
        mapRef.current.invalidateSize();
      }
    }, 200);
  }, []);

  // Gérer la sortie du plein écran avec Escape
  useEffect(() => {
    if (!isFullscreen) return;
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        e.stopPropagation(); // Empêcher la fermeture du modal parent
        setIsFullscreen(false);
        setTimeout(() => {
          if (mapRef.current) {
            mapRef.current.invalidateSize();
          }
        }, 200);
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isFullscreen]);

  // CLEANUP CRITIQUE : Nettoyer toutes les instances Leaflet au démontage
  // Cela garantit qu'aucun container .leaflet-container ne reste dans le DOM
  useEffect(() => {
    return () => {
      // Désactiver le mode plein écran
      setIsFullscreen(false);
      
      // Nettoyer l'instance Leaflet si elle existe
      if (mapRef.current) {
        try {
          // Retirer toutes les couches et événements
          mapRef.current.off();
          mapRef.current.remove();
        } catch (e) {
          console.warn('Erreur lors du nettoyage de la carte Leaflet:', e);
        }
        mapRef.current = null;
      }
      
      // Nettoyer les marqueurs
      markersRef.current.forEach(marker => {
        if (marker && mapRef.current) {
          try {
            mapRef.current.removeLayer(marker);
          } catch (e) {
            // Ignorer les erreurs
          }
        }
      });
      markersRef.current = [];
      
      // Nettoyer l'instance React-Leaflet
      mapInstanceRef.current = null;
      
      // NETTOYAGE DOM : Supprimer uniquement les containers Leaflet résiduels DANS LES MODALES FERMÉES
      // Ne PAS toucher aux cartes de la vue d'ensemble ou autres cartes actives
      // IMPORTANT: Délai plus long pour s'assurer que la modale est complètement fermée
      setTimeout(() => {
        const leafletContainers = document.querySelectorAll('.leaflet-container');
        leafletContainers.forEach((container) => {
          try {
            // PROTECTION: Ne JAMAIS supprimer la carte de la vue d'ensemble
            // Elle a une key stable "zones-overview-map" et doit rester
            const isOverviewMap = container.closest('[data-overview-map]') ||
                                 container.parentElement?.querySelector('[data-overview-map]');
            if (isOverviewMap) {
              return; // Ne pas toucher à la carte de la vue d'ensemble
            }
            
            // Vérifier si le container est dans une modale fermée (non visible)
            const modal = container.closest('[role="dialog"]') || 
                         container.closest('.fixed.inset-0.z-50');
            
            if (modal) {
              // Vérifier si la modale est visible (display: none ou hidden)
              const modalStyle = window.getComputedStyle(modal);
              const isModalHidden = modalStyle.display === 'none' || 
                                   modalStyle.visibility === 'hidden' ||
                                   modal.classList.contains('hidden') ||
                                   !modal.offsetParent; // offsetParent est null si l'élément est caché
              
              // Vérifier aussi si le container a l'attribut data-modal-map-container
              // (c'est notre marqueur pour les cartes dans les modales)
              const isModalMap = container.closest('[data-modal-map-container]');
              
              // Supprimer uniquement si c'est une carte de modale ET que la modale est fermée
              if (isModalMap && isModalHidden) {
                container.remove();
              }
            }
          } catch (e) {
            // Ignorer les erreurs
          }
        });
      }, 200); // Délai plus long pour être sûr que la modale est fermée
    };
  }, []); // Se déclenche uniquement au démontage du composant

  // Convertir le polygone au format Leaflet pour l'affichage
  const leafletPolygon = toLeafletFormat(polygon);

  const mapContent = (
    <MapContainer
      // KEY STABLE: change uniquement à l'ouverture/fermeture de la modale (ou changement de zone en édition)
      key={`zone-map-${mapInstanceKey}-${isFullscreen ? 'fullscreen' : 'normal'}`}
      center={center}
      zoom={zoom}
      style={{ height: '100%', width: '100%' }}
      preferCanvas={false} // Utiliser SVG pour de meilleures performances de clics
      scrollWheelZoom={true} // Garder le zoom avec la molette actif
      // Ne pas passer dragging et doubleClickZoom comme props car ils changent
      // et causent un remount. Gérer dynamiquement dans MapClickHandler
      whenCreated={(map) => {
        // Nettoyer l'ancienne instance si elle existe (sécurité)
        if (mapRef.current && mapRef.current !== map) {
          try {
            mapRef.current.off();
            mapRef.current.remove();
          } catch (e) {
            console.warn('Nettoyage de l\'ancienne instance Leaflet:', e);
          }
        }
        
        // Stocker les références
        mapRef.current = map;
        mapInstanceRef.current = map;
        
        // Reset le flag pour permettre un nouveau calcul de taille
        hasInvalidatedRef.current = false;

        // IMPORTANT: invalidateSize une fois la modale rendue
        requestAnimationFrame(() => {
          setTimeout(() => {
            try {
              map.invalidateSize(true);
            } catch (e) {
              // ignore
            }
          }, 0);
        });
      }}
    >
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />
      
      <MapClickHandler />
      
      {/* PANE zones existantes (fond) */}
      <Pane name="zonesPane" style={{ zIndex: 400 }}>
        {existingZones.map((existingZone) => {
          if (!existingZone?.polygon) return null;
          const normalized = normalizePolygonFromApi(existingZone.polygon);
          const existingLeafletPolygon = toLeafletFormat(normalized);
          if (existingLeafletPolygon.length < 2) return null;

          return (
            <Polygon
              key={`existing-zone-${existingZone.id}`}
              positions={existingLeafletPolygon}
              pathOptions={{
                color: '#10b981',
                opacity: 0.7,
                fillColor: '#10b981',
                fillOpacity: 0.08,
                weight: 2,
              }}
              interactive={false}
              pane="zonesPane"
            />
          );
        })}
      </Pane>
      
      {/* PANE dessin (au-dessus) */}
      <Pane name="drawPane" style={{ zIndex: 650 }}>
        {leafletPolygon.length >= 2 && (
          <>
            {isDrawing ? (
              <Polyline
                positions={leafletPolygon}
                pathOptions={{ color: '#3b82f6', weight: 3, opacity: 0.9 }}
                pane="drawPane"
              />
            ) : (
              <Polygon
                positions={leafletPolygon}
                pathOptions={{
                  color: '#3b82f6',
                  opacity: 0.9,
                  fillColor: '#3b82f6',
                  fillOpacity: 0.18,
                  weight: 2,
                }}
                pane="drawPane"
              />
            )}
          </>
        )}
      </Pane>
    </MapContainer>
  );

  // Contenu plein écran (rendu via portal uniquement si isFullscreen est true)
  const fullscreenContent = isFullscreen ? createPortal(
    <div 
      className="fixed inset-0 z-[9999] bg-white"
      style={{ 
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999
      }}
      onClick={(e) => e.stopPropagation()} // Empêcher les clics de passer à travers
    >
      <div className="h-full flex flex-col">
        {/* En-tête plein écran */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50 flex-shrink-0">
          <h3 className="text-lg font-semibold text-gray-900">
            Éditeur de zone géographique - Mode plein écran
          </h3>
          <button
            type="button"
            onClick={toggleFullscreen}
            className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Minimize2 className="h-4 w-4" />
            <span>Quitter le plein écran (Échap)</span>
          </button>
        </div>
        
        {/* Contrôles plein écran */}
        <div className="flex items-center space-x-2 flex-wrap gap-2 p-4 border-b border-gray-200 bg-white flex-shrink-0">
          <button
            type="button"
            onClick={() => setIsDrawing(!isDrawing)}
            className={`px-4 py-2 text-sm font-medium rounded-md ${
              isDrawing
                ? 'bg-red-600 text-white hover:bg-red-700'
                : 'bg-blue-600 text-white hover:bg-blue-700'
            }`}
          >
            {isDrawing ? 'Arrêter le dessin' : 'Dessiner un polygone'}
          </button>
        
          {polygon.length > 0 && (
            <>
              <button
                type="button"
                onClick={handleRemoveLastPoint}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Supprimer dernier point
              </button>
              
              {polygon.length >= 3 && isDrawing && (
                <button
                  type="button"
                  onClick={handleClosePolygon}
                  className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200"
                >
                  Fermer le polygone
                </button>
              )}
              
              <button
                type="button"
                onClick={handleClear}
                className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
              >
                Effacer tout
              </button>
            </>
          )}
          
          <span className="text-sm text-gray-600">
            {polygon.length} point{polygon.length !== 1 ? 's' : ''}
          </span>
        </div>
        
        {/* Carte plein écran */}
        <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
          <div style={{ height: '100%', width: '100%' }} className="border-t border-gray-300">
            {mapContent}
          </div>
        </div>

        {/* Instructions plein écran */}
        {isDrawing && (
          <div className="bg-blue-50 border-t border-blue-200 p-3 flex-shrink-0">
            <p className="text-sm text-blue-800">
              <strong>Instructions :</strong> Cliquez sur la carte pour ajouter des points au polygone. 
              Vous pouvez ajouter autant de points que nécessaire. Cliquez sur "Fermer le polygone" (disponible à partir de 3 points) pour terminer le dessin.
            </p>
          </div>
        )}

        {/* Validation plein écran */}
        {polygon.length > 0 && polygon.length < 3 && isDrawing && (
          <div className="bg-yellow-50 border-t border-yellow-200 p-3 flex-shrink-0">
            <p className="text-sm text-yellow-800">
              <strong>Attention :</strong> Un polygone doit contenir au moins 3 points pour être fermé. Continuez à ajouter des points.
            </p>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      {/* Mode plein écran (via portal) */}
      {fullscreenContent}

      {/* Mode normal */}
      {!isFullscreen && (
        <div className="space-y-4" style={{ position: 'relative', zIndex: 1 }}>
          {/* Contrôles */}
          <div className="flex items-center space-x-2 flex-wrap gap-2" style={{ position: 'relative', zIndex: 2 }}>
            <button
              type="button"
              onClick={() => {
                // démarrer/arrêter le dessin réinitialise le mode pan
                setIsPanning(false);
                setIsDrawing(!isDrawing);
              }}
              className={`px-4 py-2 text-sm font-medium rounded-md ${
                isDrawing
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {isDrawing ? 'Arrêter le dessin' : 'Dessiner un polygone'}
            </button>

            {isDrawing && (
              <button
                type="button"
                onClick={() => setIsPanning((v) => !v)}
                className={`px-4 py-2 text-sm font-medium rounded-md ${
                  isPanning
                    ? 'bg-gray-900 text-white hover:bg-gray-800'
                    : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                }`}
                title="Permet de déplacer la carte pendant le dessin"
              >
                {isPanning ? 'Reprendre le dessin' : 'Déplacer la carte'}
              </button>
            )}
            
            <button
              type="button"
              onClick={toggleFullscreen}
              className="flex items-center space-x-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              title="Mode plein écran (Appuyez sur Échap pour quitter)"
            >
              <Maximize2 className="h-4 w-4" />
              <span>Plein écran</span>
            </button>
          
            {polygon.length > 0 && (
              <>
                <button
                  type="button"
                  onClick={handleRemoveLastPoint}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                >
                  Supprimer dernier point
                </button>
                
                {polygon.length >= 3 && isDrawing && (
                  <button
                    type="button"
                    onClick={handleClosePolygon}
                    className="px-4 py-2 text-sm font-medium text-green-700 bg-green-100 rounded-md hover:bg-green-200"
                  >
                    Fermer le polygone
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={handleClear}
                  className="px-4 py-2 text-sm font-medium text-red-700 bg-red-100 rounded-md hover:bg-red-200"
                >
                  Effacer tout
                </button>
              </>
            )}
            
            <span className="text-sm text-gray-600">
              {polygon.length} point{polygon.length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Carte */}
          {/* IMPORTANT: height fixe (pas auto) pour éviter les problèmes de calcul Leaflet */}
          <div 
            data-modal-map-container
            style={{ 
              height: typeof height === 'string' ? height : `${height}px`, 
              width: '100%',
              position: 'relative',
              zIndex: 1,
              isolation: 'isolate'
            }} 
            className="border border-gray-300 rounded-md overflow-hidden"
          >
            <div style={{ position: 'relative', height: '100%', width: '100%', zIndex: 1 }}>
              {mapContent}
            </div>
          </div>

          {/* Instructions */}
          {isDrawing && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <p className="text-sm text-blue-800">
                <strong>Instructions :</strong> Cliquez sur la carte pour ajouter des points au polygone. 
                Vous pouvez ajouter autant de points que nécessaire. Cliquez sur "Fermer le polygone" (disponible à partir de 3 points) pour terminer le dessin.
              </p>
            </div>
          )}

          {/* Validation */}
          {polygon.length > 0 && polygon.length < 3 && isDrawing && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
              <p className="text-sm text-yellow-800">
                <strong>Attention :</strong> Un polygone doit contenir au moins 3 points pour être fermé. Continuez à ajouter des points.
              </p>
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default ZonePolygonEditor;
